import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Paper,
  Text,
  Table,
  Button,
  Badge,
  Group,
  Avatar,
  Box,
  Alert,
  Loader,
  ActionIcon,
  Tooltip,
  Modal,
} from "@mantine/core";
import {
  IconUser,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconClipboardList,
  IconRefresh,
  IconTrash,
  IconChevronRight,
  IconUpload,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { notifyError, notifySuccess } from "../../utils/notify";
import moment from "moment-timezone";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import { useResponsive } from "../../context/ResponsiveContext";
import { Employee } from "../../types/salon";
import {
  TimeCard,
  TimesheetData,
  PayPeriodTimeCard,
  TimeEntryDetail,
  TimeCardApprovalStatus,
} from "../../types/timeTracking";
import styles from "./ApproveTimesheetView.module.css";

interface ApproveTimesheetViewProps {
  eligibleEmployees: Employee[];
  timezone: string;
  timesheetData: TimesheetData;
  isLoading: boolean;
  companyId: string;
  payPeriodId: string;
  onRefresh: () => Promise<TimesheetData>;
}

/**
 * Get approval status badge properties
 */
function getApprovalBadgeProps(status: TimeCardApprovalStatus): {
  color: string;
  label: string;
} {
  switch (status) {
    case "Approved":
      return { color: "green", label: "Approved" };
    case "Synced":
      return { color: "blue", label: "Synced" };
    case "Processing":
      return { color: "cyan", label: "Processing" };
    case "Complete":
      return { color: "teal", label: "Complete" };
    default:
      return { color: "yellow", label: "Unapproved" };
  }
}

/**
 * Format time from time string to local display
 */
function formatTime(timeString: string | null, timezone: string): string {
  if (!timeString) return "-";
  return moment(timeString, ["HH:mm:ss", moment.ISO_8601])
    .tz(timezone)
    .format("h:mm A");
}

/**
 * Format hours with optional overtime
 */
function formatHours(regularHours: number, overtimeHours: number): string {
  const regularFormatted = regularHours.toFixed(2);
  if (overtimeHours > 0) {
    return `${regularFormatted} hrs (+ ${overtimeHours.toFixed(2)} OT hrs)`;
  }
  return `${regularFormatted} hrs`;
}

export default function ApproveTimesheetView({
  eligibleEmployees,
  timezone,
  timesheetData,
  isLoading,
  companyId,
  payPeriodId,
  onRefresh,
}: ApproveTimesheetViewProps) {
  const { isMobile } = useResponsive();
  const {
    updateTimeCardApprovals,
    deleteTimeEntry,
    syncTimeCardsToPayroll,
    getEmployeePayPeriodTimeCard,
  } = useApp();
  const salonContext = useSalon() as any;
  const { currentUser } = salonContext;

  const isOwnerOrManager =
    currentUser?.role === "Owner" || currentUser?.role === "Manager";

  // Local state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [deletingEntryId, setDeletingEntryId] = useState<string | null>(null);
  const [entryPendingDelete, setEntryPendingDelete] = useState<string | null>(
    null,
  );
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null,
  );

  // Detail view state
  const [detailTimeCard, setDetailTimeCard] =
    useState<PayPeriodTimeCard | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [expandedDateIds, setExpandedDateIds] = useState<Set<string>>(
    new Set(),
  );

  // Use data from props
  const timeCards = timesheetData.timeCards;

  // Auto-select first employee on mount
  useEffect(() => {
    if (eligibleEmployees.length > 0 && !selectedEmployeeId) {
      setSelectedEmployeeId(eligibleEmployees[0].id);
    }
  }, [eligibleEmployees, selectedEmployeeId]);

  // Handle refresh button click
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
      // Also refresh detail if viewing one
      if (selectedEmployeeId && detailTimeCard) {
        await fetchEmployeeDetail(selectedEmployeeId);
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  // Find timeCard for selected employee
  const selectedEmployee = eligibleEmployees.find(
    (emp) => emp.id === selectedEmployeeId,
  );
  const selectedRollfiId = selectedEmployee?.rollfi__user_id;
  const selectedTimeCard = selectedRollfiId
    ? timeCards.find((tc) => tc.userId === selectedRollfiId)
    : null;

  // Fetch employee detail (drill-down into time entries by date)
  const fetchEmployeeDetail = useCallback(
    async (employeeId: string) => {
      const emp = eligibleEmployees.find((e) => e.id === employeeId);
      if (!emp?.rollfi__user_id) return;

      try {
        setIsLoadingDetail(true);
        const response = await getEmployeePayPeriodTimeCard(
          emp.rollfi__user_id,
          companyId,
          payPeriodId,
        );
        setDetailTimeCard(response?.payPeriodTimeCard ?? null);
      } catch (err) {
        console.error("Error fetching employee time card detail:", err);
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [eligibleEmployees, companyId, payPeriodId, getEmployeePayPeriodTimeCard],
  );

  // Handle employee selection — useEffect below picks up the new id and
  // triggers fetchEmployeeDetail, so we don't fetch directly here.
  const handleSelectEmployee = (employee: Employee) => {
    setSelectedEmployeeId(employee.id);
    setDetailTimeCard(null);
    setExpandedDateIds(new Set());
  };

  // Load detail when employee changes
  useEffect(() => {
    if (selectedEmployeeId) {
      fetchEmployeeDetail(selectedEmployeeId);
    }
  }, [selectedEmployeeId, fetchEmployeeDetail]);

  // Toggle expand date
  const toggleExpandDate = (workDate: string) => {
    setExpandedDateIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(workDate)) {
        newSet.delete(workDate);
      } else {
        newSet.add(workDate);
      }
      return newSet;
    });
  };

  // Handle approve/reject
  const handleApproveReject = async (isApproved: boolean) => {
    if (!selectedTimeCard) return;

    try {
      setIsActionLoading(true);
      const response = await updateTimeCardApprovals(
        companyId,
        payPeriodId,
        [selectedTimeCard.timeCardId],
        isApproved,
      );

      const result = response.timeCards[0];
      if (result?.success) {
        notifications.show({
          title: isApproved ? "Time Card Approved" : "Approval Reset",
          message: `Time card ${isApproved ? "approved" : "reset to unapproved"} successfully`,
          color: isApproved ? "var(--color-success)" : "var(--color-warning)",
        });
        await onRefresh();
        if (selectedEmployeeId) {
          await fetchEmployeeDetail(selectedEmployeeId);
        }
      } else if (result?.message) {
        notifyError(result.message, { title: "Error" });
      }
    } catch (err) {
      console.error("Error updating time card approval:", err);
      notifyError(
        err instanceof Error ? err.message : "Failed to update approval",
        { title: "Error" },
      );
    } finally {
      setIsActionLoading(false);
    }
  };

  // Handle sync to payroll
  const handleSyncToPayroll = async () => {
    if (!selectedTimeCard) return;

    try {
      setIsSyncing(true);
      const response = await syncTimeCardsToPayroll(companyId, payPeriodId, [
        selectedTimeCard.timeCardId,
      ]);

      const result = response.timeCards[0];
      if (result?.success) {
        notifySuccess("Time card hours synced to payroll successfully", {
          title: "Synced to Payroll",
        });
        await onRefresh();
      } else if (result?.message) {
        notifyError(result.message, { title: "Sync Error" });
      }
    } catch (err) {
      console.error("Error syncing to payroll:", err);
      notifyError(
        err instanceof Error ? err.message : "Failed to sync to payroll",
        { title: "Error" },
      );
    } finally {
      setIsSyncing(false);
    }
  };

  // Handle delete time entry
  const handleDeleteEntry = async (timeEntryId: string) => {
    try {
      setDeletingEntryId(timeEntryId);
      const response = await deleteTimeEntry(timeEntryId);

      if (response.timeEntry.status === "Deleted") {
        notifySuccess(response.timeEntry.message, { title: "Entry Deleted" });
        await onRefresh();
        if (selectedEmployeeId) {
          await fetchEmployeeDetail(selectedEmployeeId);
        }
      }
    } catch (err) {
      console.error("Error deleting entry:", err);
      notifyError(
        err instanceof Error ? err.message : "Failed to delete entry",
        { title: "Error" },
      );
    } finally {
      setDeletingEntryId(null);
      setEntryPendingDelete(null);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box className={styles.loadingContainer}>
        <Loader size="lg" color="teal" />
        <Text c="dimmed">Loading timesheet...</Text>
      </Box>
    );
  }

  // No employees
  if (eligibleEmployees.length === 0) {
    return (
      <Alert
        color="yellow"
        title="No Eligible Staff"
        icon={<IconAlertCircle />}
      >
        No staff members are currently set up for time tracking. Staff members
        must complete onboarding before appearing here. <br />
        Please reach out to our support team (
        <Text
          component="a"
          href="mailto:contact@getpagoda.com"
          c="blue"
          td="underline"
        >
          contact@getpagoda.com
        </Text>
        ) if you need assistance.
      </Alert>
    );
  }

  return (
    <Box className={styles.container}>
      {/* Employee Selection Panel */}
      <Box className={styles.employeePanel}>
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <IconUser size={20} />
            <Text size="md" fw={500}>
              Employees
            </Text>
            <Button
              variant="subtle"
              size="compact-xs"
              color="gray"
              onClick={handleRefresh}
              loading={isRefreshing}
              p={4}
            >
              <IconRefresh size={16} />
            </Button>
          </Group>
          <Badge color="gray" variant="light" size="sm">
            {eligibleEmployees.length}
          </Badge>
        </Group>

        <Box className={styles.employeeList}>
          {eligibleEmployees.map((employee) => {
            const isSelected = selectedEmployeeId === employee.id;
            const empRollfiId = employee.rollfi__user_id;
            const empTimeCard = empRollfiId
              ? timeCards.find((tc) => tc.userId === empRollfiId)
              : null;

            return (
              <Paper
                key={employee.id}
                className={`${styles.employeeItem} ${isSelected ? styles.employeeItemSelected : ""}`}
                withBorder
                onClick={() => handleSelectEmployee(employee)}
              >
                <Box className={styles.employeeItemContent}>
                  <Avatar size="md" color="gray" radius="xl">
                    <IconUser size={20} />
                  </Avatar>
                  <Box className={styles.employeeInfo}>
                    <Text className={styles.employeeName} fw={500}>
                      {employee.first_name} {employee.last_name}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {empTimeCard && (
                        <>
                          {empTimeCard.regularHours.toFixed(1)} hrs
                          {empTimeCard.overtimeHours > 0 &&
                            ` + ${empTimeCard.overtimeHours.toFixed(1)} OT`}
                        </>
                      )}
                    </Text>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Box>

      {/* Timesheet Detail Panel */}
      <Box className={styles.timesheetPanel} py="md" px="xl">
        {selectedEmployee && selectedTimeCard ? (
          <Stack gap="md">
            {/* Header with actions */}
            <Box className={styles.tableHeader}>
              <Box className={styles.tableHeaderInfo}>
                <Avatar size="lg" color="teal" radius="xl">
                  <IconUser size={24} />
                </Avatar>
                <Box>
                  <Text fw={600} size="lg">
                    {selectedEmployee.first_name} {selectedEmployee.last_name}
                  </Text>
                  <Group gap="xs">
                    <Text size="sm" c="dimmed">
                      {formatHours(
                        selectedTimeCard.regularHours,
                        selectedTimeCard.overtimeHours,
                      )}
                    </Text>
                    <Badge
                      {...getApprovalBadgeProps(
                        selectedTimeCard.approvalStatus,
                      )}
                      variant="light"
                      size="sm"
                    />
                  </Group>
                </Box>
              </Box>

              {isOwnerOrManager && (
                <Box className={styles.actionButtons}>
                  {selectedTimeCard.approvalStatus === "Unapproved" && (
                    <Button
                      leftSection={<IconCheck size={16} />}
                      color="var(--color-primary)"
                      size={isMobile ? "xs" : "sm"}
                      loading={isActionLoading}
                      onClick={() => handleApproveReject(true)}
                    >
                      Approve
                    </Button>
                  )}
                  {selectedTimeCard.approvalStatus === "Approved" && (
                    <>
                      <Button
                        leftSection={<IconX size={16} />}
                        color="orange"
                        variant="light"
                        size={isMobile ? "xs" : "sm"}
                        loading={isActionLoading}
                        onClick={() => handleApproveReject(false)}
                      >
                        Reset Approval
                      </Button>
                      <Button
                        leftSection={<IconUpload size={16} />}
                        color="blue"
                        size={isMobile ? "xs" : "sm"}
                        loading={isSyncing}
                        onClick={handleSyncToPayroll}
                      >
                        Sync to Payroll
                      </Button>
                    </>
                  )}
                  {selectedTimeCard.approvalStatus === "Synced" && (
                    <Button
                      leftSection={<IconUpload size={16} />}
                      color="blue"
                      variant="light"
                      size={isMobile ? "xs" : "sm"}
                      loading={isSyncing}
                      onClick={handleSyncToPayroll}
                    >
                      Re-sync to Payroll
                    </Button>
                  )}
                </Box>
              )}
            </Box>

            {/* Detail Table - Work Dates & Entries */}
            {isLoadingDetail ? (
              <Box className={styles.loadingContainer}>
                <Loader size="sm" color="teal" />
                <Text c="dimmed" size="sm">
                  Loading entries...
                </Text>
              </Box>
            ) : detailTimeCard?.workDates &&
              detailTimeCard.workDates.length > 0 ? (
              <Box className={styles.tableContainer}>
                <Table
                  className={styles.table}
                  striped
                  highlightOnHover
                  withTableBorder
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th className={styles.expandCell}></Table.Th>
                      <Table.Th className={styles.dateCell}>Date</Table.Th>
                      <Table.Th className={styles.hoursCell}>Regular</Table.Th>
                      <Table.Th className={styles.hoursCell}>Overtime</Table.Th>
                      <Table.Th className={styles.hoursCell}>Entries</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {detailTimeCard.workDates.map((wd) => {
                      const isExpanded = expandedDateIds.has(wd.workDate);
                      return (
                        <React.Fragment key={wd.workDate}>
                          <Table.Tr>
                            <Table.Td className={styles.expandCell}>
                              {wd.timeEntries.length > 0 && (
                                <ActionIcon
                                  variant="subtle"
                                  size="sm"
                                  onClick={() => toggleExpandDate(wd.workDate)}
                                >
                                  <IconChevronRight
                                    size={18}
                                    style={{
                                      transition: "transform 0.1s ease-in-out",
                                      transform: isExpanded
                                        ? "rotate(90deg)"
                                        : "rotate(0deg)",
                                    }}
                                  />
                                </ActionIcon>
                              )}
                            </Table.Td>
                            <Table.Td className={styles.dateCell}>
                              {moment(wd.workDate).format("MM/DD/YYYY")}
                            </Table.Td>
                            <Table.Td className={styles.hoursCell}>
                              {wd.regularHours.toFixed(2)} hrs
                            </Table.Td>
                            <Table.Td className={styles.hoursCell}>
                              {wd.overtimeHours > 0
                                ? `${wd.overtimeHours.toFixed(2)} hrs`
                                : "-"}
                            </Table.Td>
                            <Table.Td className={styles.hoursCell}>
                              {wd.timeEntries.length}
                            </Table.Td>
                          </Table.Tr>
                          {isExpanded &&
                            wd.timeEntries.map((entry) => (
                              <Table.Tr
                                key={entry.timeEntryId}
                                className={styles.expandedRow}
                              >
                                <Table.Td />
                                <Table.Td colSpan={3}>
                                  <Group gap="md">
                                    <Text size="sm">
                                      {formatTime(entry.clockInTime, timezone)}{" "}
                                      →{" "}
                                      {formatTime(entry.clockOutTime, timezone)}
                                    </Text>
                                    {entry.breaks.length > 0 && (
                                      <Text size="xs" c="dimmed">
                                        ({entry.breaks.length} break
                                        {entry.breaks.length > 1 ? "s" : ""})
                                      </Text>
                                    )}
                                  </Group>
                                </Table.Td>
                                <Table.Td>
                                  {isOwnerOrManager &&
                                    selectedTimeCard.approvalStatus ===
                                      "Unapproved" && (
                                      <Tooltip label="Delete entry" withArrow>
                                        <ActionIcon
                                          color="red"
                                          variant="subtle"
                                          size="sm"
                                          loading={
                                            deletingEntryId ===
                                            entry.timeEntryId
                                          }
                                          disabled={deletingEntryId !== null}
                                          onClick={() =>
                                            setEntryPendingDelete(
                                              entry.timeEntryId,
                                            )
                                          }
                                        >
                                          <IconTrash size={16} />
                                        </ActionIcon>
                                      </Tooltip>
                                    )}
                                </Table.Td>
                              </Table.Tr>
                            ))}
                        </React.Fragment>
                      );
                    })}
                    {/* Totals Row */}
                    <Table.Tr className={styles.totalsRow}>
                      <Table.Td />
                      <Table.Td>
                        <Text fw={600}>Total</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text fw={600}>
                          {detailTimeCard.regularHours.toFixed(2)} hrs
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text fw={600}>
                          {detailTimeCard.overtimeHours > 0
                            ? `${detailTimeCard.overtimeHours.toFixed(2)} hrs`
                            : "-"}
                        </Text>
                      </Table.Td>
                      <Table.Td />
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Box>
            ) : (
              <Box className={styles.emptyState}>
                <IconClipboardList
                  size={48}
                  color="var(--mantine-color-gray-5)"
                />
                <Text c="dimmed" mt="md">
                  No time entries for this pay period
                </Text>
              </Box>
            )}
          </Stack>
        ) : selectedEmployee && !selectedTimeCard ? (
          <Box className={styles.emptyState}>
            <IconClipboardList size={48} color="var(--mantine-color-gray-5)" />
            <Text c="dimmed" mt="md">
              No time card found for this employee in this pay period
            </Text>
          </Box>
        ) : (
          <Box className={styles.emptyState}>
            <IconUser size={48} color="var(--mantine-color-gray-5)" />
            <Text c="dimmed" mt="md">
              Select an employee to view their timesheet
            </Text>
          </Box>
        )}
      </Box>

      {/* Delete confirmation modal */}
      <Modal
        opened={entryPendingDelete !== null}
        onClose={() => setEntryPendingDelete(null)}
        title="Delete Time Entry?"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            This will permanently delete the time entry. This action cannot be
            undone.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setEntryPendingDelete(null)}
              disabled={deletingEntryId !== null}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={deletingEntryId !== null}
              onClick={() => {
                if (entryPendingDelete) {
                  handleDeleteEntry(entryPendingDelete);
                }
              }}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
